/**
 * The corridor unrolled: the whole `dz` matrix as one heat strip, chainage
 * left to right, offset top to bottom, LEFT of the alignment at the TOP
 * (strip-map spec §3.1).
 *
 * THREE layers, deliberately:
 *  - an image CANVAS carrying the matrix, drawn once per data/tolerance/span
 *    change through `stripModel.buildStripPixels` and blitted with
 *    `imageSmoothingEnabled = false`. A 250 x 61 matrix is 15 000 cells and
 *    15 000 SVG rects is a layout the browser re-lays-out on every hover.
 *  - an overlay CANVAS for the marks that move with the pointer — the hover
 *    crosshair, the walk rule, the brush dimming — so a mouse move never
 *    re-rasterises the image (spec §3.6).
 *  - SVG and DOM siblings for the axes, the legend, the hatches and the
 *    verdict row: they are text and they are what a test can assert.
 *
 * The interaction is a copy of `StationVolumeChart`'s, not a variation on it
 * (spec §4): the 3D bands, the table and the chart must not be able to tell
 * which pane the user is in.
 */
import { alignmentWalkAtom } from '@/atoms/alignment.atom';
import { stationVolumeHoverBinAtom, stationVolumeSelectionAtom } from '@/atoms/stationVolumes.atom';
import {
  CUT_LEGEND_SHORT_KEY,
  FILL_LEGEND_SHORT_KEY,
  signIsCut,
} from '@/lib/cutFillConvention';
import { binCentreStation, binIndexForStation } from '@/lib/stationVolumes/aggregate';
import { equationMarkers, thinLabels } from '@/lib/stationVolumes/chartLayout';
import { formatFixed, staOnly, STATION_VOLUME_DISPLAY_DECIMALS } from '@/lib/stationVolumes/format';
import {
  binIndexAtFraction,
  buildStripLut,
  buildStripPixels,
  imageRowForOffsetIndex,
  lowCoverageRuns,
  markSpanPx,
  MIN_MARK_PX,
  offsetIndexAtFraction,
  offsetMetresAt,
  sameValueRuns,
  STA_LABEL_MIN_GAP_PX,
  STRIP_MARGIN,
  VERDICT_ROW_PX,
  xForStation,
  yForOffsetM,
  type StripMatrix,
} from '@/lib/stationVolumes/stripModel';
import type { StationVolumesAnalysisKind, StationVolumesJson } from '@/lib/stationVolumes/types';
import { useSvPalette } from '@/lib/stationVolumes/useSvPalette';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { SEVERITY } from './VerdictChip';

export interface StripMapCanvasProps {
  json: StationVolumesJson;
  matrix: StripMatrix;
  /**
   * The `zTop` / `zBottom` planes behind `matrix`, in the SAME row-major
   * `nStations × nOffsets` layout as its `dz` values — the tooltip quotes both
   * elevations beside the difference (spec §3.4), because "−60 mm" without the
   * two levels it came from cannot be checked against a survey.
   *
   * Optional: a stack strip (Task 8) subtracts two as-built planes and has no
   * single pair to quote, and a caller that has not decoded them still gets a
   * working strip with the two rows simply absent.
   */
  zTop?: Float32Array;
  zBottom?: Float32Array;
  layerId: string;
  width: number;
  height: number;
  /** Half-width of the neutral band, in metres. 0 disables it (spec §3.2). */
  toleranceM: number;
  /** Half-width of the diverging ramp, in metres (spec §3.3). */
  spanM: number;
  offsetStartM: number;
  offsetStepM: number;
  kind: StationVolumesAnalysisKind;
  swathLeftM: number;
  swathRightM: number;
  referenceExtentLeftM?: number | null;
  referenceExtentRightM?: number | null;
  /**
   * Replaces the Δz legend sentence. The default one names the two surfaces
   * ("as-built above design"), which is true of a `dz` strip and FALSE of a
   * stack's thickness strip (Task 8): there, blue is a THICKER layer, whose
   * as-built top is above design, not below it. A caller drawing something
   * other than `dz` hands the sentence in rather than letting the pane state
   * the inverse of what it drew.
   */
  legendText?: string;
}

/**
 * How near a brush edge a pointer must land to grab that handle. The chart's
 * number (`StationVolumeChart.tsx`), because a brush dragged from the chart
 * and continued on the strip must feel like one brush.
 */
/** Never collapse the strip to nothing to make room for a caption: below this
 *  the pane is too short for both and the strip wins. */
const MIN_STRIP_PLOT_PX = 80;
/** The colour key beside the legend text. Small: it is a key, not a chart. */
const RAMP_W_PX = 96;
const RAMP_H_PX = 8;
const HANDLE_GRAB_PX = 12;
/** The wash the brush lays over the bins OUTSIDE the selection. */
const DIM_ALPHA = 0.55;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

export const StripMapCanvas = ({
  json,
  matrix,
  zTop,
  zBottom,
  layerId,
  width: outerWidth,
  height: outerHeight,
  toleranceM,
  spanM,
  offsetStartM,
  offsetStepM,
  kind,
  swathLeftM,
  swathRightM,
  referenceExtentLeftM,
  referenceExtentRightM,
  legendText,
}: StripMapCanvasProps) => {

  // The legend and the orientation note sit BELOW the strip in this same
  // column. Let CSS split the space — the plot area is `flex-1`, the legend
  // `shrink-0` — and measure the PLOT AREA rather than arithmetic on the
  // height the parent passes down. Subtracting a measured legend height from
  // the parent's box was the first attempt and it was wrong twice over: it
  // depends on that box resolving to the height you expect, and it fights the
  // very layout that already knows the answer.
  //
  // The same applies to WIDTH: the size the parent measured on its own box was
  // stale here too, leaving the strip ~1370 px wide in a 2000 px pane. The plot
  // box owns both dimensions; the props are only a fallback, used whenever the
  // measurement is 0 — before the first observation, and under jsdom, which has
  // no layout at all. Every test therefore sees exactly the geometry it always
  // saw.
  const plotBoxRef = useRef<HTMLDivElement | null>(null);
  const [measured, setMeasured] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = plotBoxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (!r) return;
      setMeasured((prev) =>
        (r.width > 0 && r.width !== prev.width) || (r.height > 0 && r.height !== prev.height)
          ? { width: r.width > 0 ? r.width : prev.width, height: r.height > 0 ? r.height : prev.height }
          : prev,
      );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const width = measured.width > 0 ? measured.width : outerWidth;
  const height = measured.height > 0 ? Math.max(MIN_STRIP_PLOT_PX, measured.height) : outerHeight;
  const { t } = useTranslation('project');
  const palette = useSvPalette();
  const [selection, setSelection] = useAtom(stationVolumeSelectionAtom);
  // Read AND write: the crosshair follows the table's and the chart's hover
  // too, so the dock is one instrument rather than four (spec §4).
  const hoverBin = useAtomValue(stationVolumeHoverBinAtom);
  const setHover = useSetAtom(stationVolumeHoverBinAtom);
  const [walk, setWalk] = useAtom(alignmentWalkAtom);
  /** The offset ROW under the pointer. Not in an atom: no other pane has an
   *  offset axis, so there is nothing to link it to. */
  const [hoverOffset, setHoverOffset] = useState<number | null>(null);
  const [cursor, setCursor] = useState<'crosshair' | 'ew-resize' | 'grabbing'>('crosshair');

  const imageRef = useRef<HTMLCanvasElement | null>(null);
  const overlayRef = useRef<HTMLCanvasElement | null>(null);
  // Only ever read inside event handlers — the React Compiler forbids reading
  // a ref during render.
  const lastCellRef = useRef<string | null>(null);
  const draggingRef = useRef(false);
  /** Owns the window listeners of an in-flight drag, so unmounting mid-drag
   *  (the dock closing under the pointer) cannot leak them. */
  const dragAbortRef = useRef<AbortController | null>(null);

  const nBins = json.bins.length;
  const nOffsets = Math.max(1, matrix.nOffsets);

  const plot = useMemo(
    () => ({
      left: STRIP_MARGIN.left,
      top: STRIP_MARGIN.top,
      width: Math.max(1, width - STRIP_MARGIN.left - STRIP_MARGIN.right),
      height: Math.max(1, height - STRIP_MARGIN.top - STRIP_MARGIN.bottom - VERDICT_ROW_PX),
    }),
    [width, height],
  );

  /** Rebuilt only when the ramp, the tolerance or the span changes (spec
   *  §3.6) — one hex parse per cell per redraw is 15 000 of them. */
  /** The colour key. Painted from the SAME `lut` as the strip itself rather
   *  than a hand-built CSS gradient, so the key cannot drift from the picture
   *  — it shows the tolerance band's grey and the ramp's exact inks, whatever
   *  the reader has set them to. */
  const rampRef = useRef<HTMLCanvasElement | null>(null);

  const lut = useMemo(
    () => buildStripLut(toleranceM, spanM, { cut: palette.cut, fill: palette.fill, neutral: palette.zero }),
    [toleranceM, spanM, palette],
  );

  useEffect(() => {
    const cv = rampRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
    cv.width = Math.round(RAMP_W_PX * dpr);
    cv.height = Math.round(RAMP_H_PX * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const entries = lut.length / 4;
    for (let x = 0; x < RAMP_W_PX; x++) {
      const i = Math.min(entries - 1, Math.round((x / (RAMP_W_PX - 1)) * (entries - 1)));
      ctx.fillStyle = `rgba(${lut[i * 4]},${lut[i * 4 + 1]},${lut[i * 4 + 2]},${lut[i * 4 + 3] / 255})`;
      ctx.fillRect(x, 0, 1, RAMP_H_PX);
    }
  }, [lut]);

  // ------------------------------------------------------------- the image
  //
  // Keyed on the DATA and the box only. A hover, a walk or a brush must never
  // land here: re-rasterising 15 000 cells per mouse move is exactly what the
  // second canvas exists to avoid.
  useEffect(() => {
    const cv = imageRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
    cv.width = Math.max(1, Math.round(width * dpr));
    cv.height = Math.max(1, Math.round(height * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    const columns = Math.min(matrix.nStations, Math.max(1, Math.round(plot.width)));
    const img = buildStripPixels(matrix, { columns, lut, spanM });
    const off = document.createElement('canvas');
    off.width = img.width;
    off.height = img.height;
    const octx = off.getContext('2d');
    if (!octx) return;
    const id = octx.createImageData(img.width, img.height);
    id.data.set(img.data);
    octx.putImageData(id, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(off, plot.left, plot.top, plot.width, plot.height);
  }, [matrix, lut, spanM, plot.left, plot.top, plot.width, plot.height, width, height]);

  // ------------------------------------------------------------- live state
  const sel = selection?.layerId === layerId ? selection : null;
  const selLo = sel ? clamp(Math.min(sel.fromBin, sel.toBin), 0, nBins - 1) : -1;
  const selHi = sel ? clamp(Math.max(sel.fromBin, sel.toBin), 0, nBins - 1) : -1;

  const walkStation = walk?.key === json.alignment.key ? walk.station : null;
  const walkBin = walkStation == null || nBins === 0 ? null : binIndexForStation(json.params, nBins, walkStation);

  // Marks are laid out per BIN while the image is drawn per matrix column: a
  // stack input has `nBins + 1` station LINES (spec §3.6), so Task 8 must map
  // lines to bins before handing the matrix over — pass it raw and every mark
  // here sits half a column off the cells it describes.
  const colW = plot.width / Math.max(1, nBins);
  const rowH = plot.height / nOffsets;
  const selX1 = sel ? plot.left + selLo * colW : 0;
  const selX2 = sel ? plot.left + (selHi + 1) * colW : 0;

  // ----------------------------------------------------------- the overlay
  //
  // Everything that moves with the pointer, on its own transparent canvas.
  // `imageRef` is never touched here.
  useEffect(() => {
    const cv = overlayRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
    cv.width = Math.max(1, Math.round(width * dpr));
    cv.height = Math.max(1, Math.round(height * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    // (a) The brush: dim what is OUTSIDE it, the same way the chart does —
    // the selection is the subject, so it keeps its full colour and the rest
    // recedes.
    if (sel && nBins > 0) {
      ctx.save();
      ctx.globalAlpha = DIM_ALPHA;
      ctx.fillStyle = palette.surface;
      if (selX1 > plot.left) ctx.fillRect(plot.left, plot.top, selX1 - plot.left, plot.height);
      const right = plot.left + plot.width;
      if (selX2 < right) ctx.fillRect(selX2, plot.top, right - selX2, plot.height);
      ctx.restore();
    }

    // (b) The hovered cell. A hover arriving from the table or the chart
    // carries a bin and no offset, so the whole column is outlined instead.
    if (hoverBin != null && hoverBin >= 0 && hoverBin < nBins) {
      ctx.save();
      ctx.strokeStyle = palette.ink;
      ctx.lineWidth = 1;
      const x = plot.left + hoverBin * colW;
      if (hoverOffset != null) {
        const row = imageRowForOffsetIndex(hoverOffset, nOffsets);
        ctx.strokeRect(x + 0.5, plot.top + row * rowH + 0.5, Math.max(1, colW - 1), Math.max(1, rowH - 1));
      } else {
        ctx.strokeRect(x + 0.5, plot.top + 0.5, Math.max(1, colW - 1), plot.height - 1);
      }
      ctx.restore();
    }

    // (c) The walked station.
    if (walkBin != null) {
      ctx.save();
      ctx.strokeStyle = palette.walk;
      ctx.lineWidth = 2;
      const x = plot.left + (walkBin + 0.5) * colW;
      ctx.beginPath();
      ctx.moveTo(x, plot.top);
      ctx.lineTo(x, plot.top + plot.height);
      ctx.stroke();
      ctx.restore();
    }
  }, [
    hoverBin,
    hoverOffset,
    walkBin,
    sel,
    selX1,
    selX2,
    nBins,
    nOffsets,
    colW,
    rowH,
    plot,
    palette,
    width,
    height,
  ]);

  // A drag in flight when this unmounts (the dock closed under the pointer)
  // would otherwise leave `pointermove` on the window for the rest of the
  // session, writing into a dead component's atoms.
  useEffect(() => () => dragAbortRef.current?.abort(), []);

  // ---------------------------------------------------------- interaction
  const binAtLocalX = useCallback(
    (x: number) => binIndexAtFraction((x - plot.left) / plot.width, nBins),
    [plot.left, plot.width, nBins],
  );
  const offsetAtLocalY = useCallback(
    (y: number) => offsetIndexAtFraction((y - plot.top) / plot.height, nOffsets),
    [plot.top, plot.height, nOffsets],
  );

  const walkToBin = useCallback(
    (i: number) => {
      const b = json.bins[i];
      if (!b) return;
      const station = binCentreStation(b);
      // `follow` is left untouched: the camera moves only if the user asked
      // it to on the walk toolbar.
      setWalk((w) => (w && w.key === json.alignment.key ? { ...w, station } : w));
    },
    [json.bins, json.alignment.key, setWalk],
  );

  /** True inside either brush edge's ±12 px grab zone. */
  const nearHandle = (x: number) =>
    !!sel && (Math.abs(x - selX1) <= HANDLE_GRAB_PX || Math.abs(x - selX2) <= HANDLE_GRAB_PX);

  const onOverlayMove = (e: PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // The cursor tracks the pointer, not the cell: you can cross into a grab
    // zone without changing bin.
    if (!draggingRef.current) setCursor(nearHandle(x) ? 'ew-resize' : 'crosshair');
    if (nBins === 0) return;
    const i = binAtLocalX(x);
    const j = offsetAtLocalY(y);
    const cell = `${i}:${j}`;
    if (cell === lastCellRef.current) return;
    lastCellRef.current = cell;
    setHover(i);
    setHoverOffset(j);
  };

  const onOverlayLeave = () => {
    lastCellRef.current = null;
    if (!draggingRef.current) setCursor('crosshair');
    setHover(null);
    setHoverOffset(null);
  };

  /**
   * Brush + click-to-walk. `pointermove`/`pointerup` go on the window (the
   * chart's overlay does the same) so a drag that leaves the pane keeps
   * tracking instead of freezing halfway.
   */
  const onOverlayDown = (e: PointerEvent<HTMLCanvasElement>) => {
    if (e.button !== 0 || nBins === 0) return;
    const originLeft = e.currentTarget.getBoundingClientRect().left;
    const downX = e.clientX - originLeft;
    const downBin = binAtLocalX(downX);

    // Grabbing an existing brush edge re-drags that edge; the opposite edge
    // becomes the anchor.
    let anchor = downBin;
    let handleDrag = false;
    if (sel) {
      if (Math.abs(downX - selX1) <= HANDLE_GRAB_PX) {
        anchor = selHi;
        handleDrag = true;
      } else if (Math.abs(downX - selX2) <= HANDLE_GRAB_PX) {
        anchor = selLo;
        handleDrag = true;
      }
    }

    let moved = handleDrag;
    draggingRef.current = true;
    setCursor('grabbing');

    // One AbortController per drag removes all three listeners at once, and
    // the unmount effect above can abort the same one.
    dragAbortRef.current?.abort();
    const ac = new AbortController();
    dragAbortRef.current = ac;

    const move = (ev: globalThis.PointerEvent) => {
      const i = binAtLocalX(ev.clientX - originLeft);
      if (i !== downBin) moved = true;
      if (moved) setSelection({ layerId, fromBin: anchor, toBin: i });
    };
    const up = (ev: globalThis.PointerEvent) => {
      ac.abort();
      if (dragAbortRef.current === ac) dragAbortRef.current = null;
      draggingRef.current = false;
      setCursor(nearHandle(ev.clientX - originLeft) ? 'ew-resize' : 'crosshair');
      if (moved) return;
      // A click, not a drag: clear the brush and walk to that bin.
      setSelection(null);
      walkToBin(binAtLocalX(ev.clientX - originLeft));
    };
    window.addEventListener('pointermove', move, { signal: ac.signal });
    window.addEventListener('pointerup', up, { signal: ac.signal });
    window.addEventListener('pointercancel', up, { signal: ac.signal });
  };

  /**
   * One tab stop, arrow-driven, with the chart's bindings to the key — `←/→`
   * step the walked station one bin and `Shift` makes that ten, unless a brush
   * exists, in which case `Shift+←/→` moves its trailing edge.
   */
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (nBins === 0) return;
    if (e.key === 'Escape') {
      if (!sel) return; // nothing of ours to clear — let the dock close
      e.preventDefault();
      e.stopPropagation();
      setSelection(null);
      return;
    }
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      e.stopPropagation();
      walkToBin(e.key === 'Home' ? 0 : nBins - 1);
      return;
    }
    const dir = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
    if (dir === 0) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.shiftKey && sel) {
      setSelection({ layerId, fromBin: sel.fromBin, toBin: clamp(sel.toBin + dir, 0, nBins - 1) });
      return;
    }
    walkToBin(clamp((walkBin ?? 0) + dir * (e.shiftKey ? 10 : 1), 0, nBins - 1));
  };

  // ---------------------------------------------------------------- marks
  // Both mappings live in `stripModel.ts` so the PNG export draws the same
  // axes from the same arithmetic (they used to be copied into both files).
  const staX = useCallback(
    (internal: number) => xForStation(internal, json.params, plot.left, plot.width),
    [json.params, plot.left, plot.width],
  );
  const offsetY = (m: number) => yForOffsetM(m, offsetStartM, offsetStepM, nOffsets, plot.top, plot.height);

  const staKeep = useMemo(
    () => thinLabels(json.bins.map((_, i) => plot.left + i * colW), STA_LABEL_MIN_GAP_PX),
    [json.bins, plot.left, colW],
  );
  const equations = useMemo(() => equationMarkers(json), [json]);
  const equationKeep = useMemo(
    () => thinLabels(equations.map((e) => staX(e.station)), STA_LABEL_MIN_GAP_PX),
    [equations, staX],
  );

  const anyVerdict = json.bins.some((b) => b.verdict);
  const axisY = plot.top + plot.height + VERDICT_ROW_PX;

  /** The low-coverage hatch and the verdict row, as RUNS with a minimum
   *  on-screen width — see the two comments at their draw sites. */
  const lowCoverageMarks = useMemo(
    () =>
      lowCoverageRuns(json.bins.map((b) => !!b.lowCoverage)).map(([start, end]) => ({
        start,
        end,
        ...markSpanPx(start, end, nBins, plot.left, plot.width),
      })),
    [json.bins, nBins, plot.left, plot.width],
  );
  const verdictRuns = useMemo(() => sameValueRuns(json.bins.map((b) => b.verdict ?? null)), [json.bins]);

  /** Where the offset axis crosses zero, clamped into the plot box. */
  const centreY = clamp(offsetY(0), plot.top, plot.top + plot.height);

  // ------------------------------------------------------------- the tip
  const hoverStationRow = hoverBin == null ? -1 : Math.min(hoverBin, matrix.nStations - 1);
  const tipBin = hoverBin == null ? null : (json.bins[hoverBin] ?? null);
  const tipCell = hoverOffset == null || hoverStationRow < 0 ? -1 : hoverStationRow * nOffsets + hoverOffset;
  const tipValue = tipCell < 0 ? Number.NaN : matrix.values[tipCell];
  const tipOffsetM = hoverOffset == null ? 0 : offsetMetresAt(hoverOffset, offsetStartM, offsetStepM);
  /** The two levels the Δz above came FROM, when the caller decoded them.
   *  `'—'` rather than a hidden row for a cell one plane missed: the row's
   *  absence would read as "this run has no top surface". */
  const tipZTop = zTop && tipCell >= 0 ? zTop[tipCell] : null;
  const tipZBottom = zBottom && tipCell >= 0 ? zBottom[tipCell] : null;
  const metres = (v: number) =>
    Number.isFinite(v) ? formatFixed(v, STATION_VOLUME_DISPLAY_DECIMALS) : '—';

  const legend =
    legendText ??
    (toleranceM > 0
      ? t('stationVolumes.strip.legend', { mm: Math.round(toleranceM * 1000) })
      : t('stationVolumes.strip.legendNoTolerance'));

  const ariaLabel = t('stationVolumes.strip.ariaLabel', {
    bins: nBins,
    from: nBins ? staOnly(json.bins[0].staStartDisplayM, 0) : '',
    to: nBins ? staOnly(json.bins[nBins - 1].staEndDisplayM, 0) : '',
  });

  return (
    <div
      className='relative flex h-full min-h-0 min-w-0 flex-col outline-none'
      data-testid='sv-strip'
      role='group'
      tabIndex={0}
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
    >
      <div ref={plotBoxRef} className='relative min-h-0 w-full flex-1'>
        <canvas
          data-testid='sv-strip-image'
          ref={imageRef}
          className='absolute inset-0'
          style={{ width, height }}
        />
        <canvas
          data-testid='sv-strip-overlay'
          ref={overlayRef}
          className='absolute inset-0'
          style={{ width, height, cursor }}
          onPointerMove={onOverlayMove}
          onPointerLeave={onOverlayLeave}
          onPointerDown={onOverlayDown}
        />
        <svg
          className='pointer-events-none absolute inset-0'
          width={width}
          height={height}
          aria-hidden='true'
          role='presentation'
        >
          <defs>
            {/* The chart's low-coverage hatch, at the same 45° and the same
                weight, so "we did not measure enough here" reads identically
                in both panes. */}
            <pattern id='sv-strip-hatch' width={5} height={5} patternTransform='rotate(45)' patternUnits='userSpaceOnUse'>
              <line x1={0} y1={0} x2={0} y2={5} stroke={palette.haul} strokeWidth={1.5} strokeOpacity={0.75} />
            </pattern>
          </defs>

          {/* The swath limits: the strip's own top and bottom edges. */}
          <line
            x1={plot.left}
            y1={plot.top}
            x2={plot.left + plot.width}
            y2={plot.top}
            stroke={palette.clear}
            strokeWidth={1}
            strokeOpacity={0.7}
          />
          <line
            x1={plot.left}
            y1={plot.top + plot.height}
            x2={plot.left + plot.width}
            y2={plot.top + plot.height}
            stroke={palette.clear}
            strokeWidth={1}
            strokeOpacity={0.7}
          />

          {/* The reference's own extent (A17): dashed, because it is what the
              coverage figures are a share OF, not a limit anyone surveyed to. */}
          {[referenceExtentLeftM, referenceExtentRightM].map((m, k) =>
            typeof m === 'number' && Number.isFinite(m) ? (
              <line
                key={k}
                data-testid='sv-strip-reference-extent'
                x1={plot.left}
                y1={offsetY(m)}
                x2={plot.left + plot.width}
                y2={offsetY(m)}
                stroke={palette.clear}
                strokeWidth={1}
                strokeDasharray='3 3'
              />
            ) : null,
          )}

          {/* Low coverage: one patch per RUN of adjacent low bins, never
              thinner than `MIN_MARK_PX` (fix wave). `MAX_BINS` is 20 000, so a
              per-bin `<rect width={colW}>` is 0.055 px on a 1 100 px pane —
              unpaintable, on exactly the long runs the hatch is for, while
              the legend goes on promising it. Twin of the same rule in
              `station_strip.py`. */}
          {lowCoverageMarks.map((m) => (
            <rect
              key={m.start}
              data-testid='sv-strip-lowcoverage'
              data-bin={m.start}
              data-bins={m.end - m.start}
              x={m.x}
              y={plot.top}
              width={m.width}
              height={plot.height}
              fill='url(#sv-strip-hatch)'
            />
          ))}

          {walkBin != null && (
            <line
              data-testid='sv-strip-walk'
              x1={plot.left + (walkBin + 0.5) * colW}
              y1={plot.top}
              x2={plot.left + (walkBin + 0.5) * colW}
              y2={plot.top + plot.height}
              stroke={palette.walk}
              strokeWidth={2}
            />
          )}

          {/* Chainage. Labels sit at each bin's START station, thinned to the
              spec's ≥ 80 px so a 4 000-bin run does not print a black bar. */}
          {json.bins.map((b, i) =>
            staKeep[i] ? (
              <g key={b.i}>
                <line
                  x1={plot.left + i * colW}
                  y1={axisY}
                  x2={plot.left + i * colW}
                  y2={axisY + 3}
                  stroke={palette.haul}
                  strokeWidth={1}
                  strokeOpacity={0.6}
                />
                <text
                  data-testid='sv-strip-sta'
                  x={plot.left + i * colW}
                  y={axisY + 12}
                  fontSize={9}
                  textAnchor='middle'
                  fill={palette.haul}
                >
                  {staOnly(b.staStartDisplayM, 0)}
                </text>
              </g>
            ) : null,
          )}

          {/* Station equations: the chainage jumps, tagged `from → to`, or the
              labels below lie about the distance between two ticks. */}
          {equations.map((e, k) => (
            <g key={e.station}>
              <line
                data-testid='sv-strip-equation'
                x1={staX(e.station)}
                y1={plot.top}
                x2={staX(e.station)}
                y2={axisY + 3}
                stroke={palette.caution}
                strokeWidth={1}
                strokeDasharray='2 2'
              />
              {equationKeep[k] && (
                <text
                  x={staX(e.station)}
                  y={axisY + 22}
                  fontSize={9}
                  textAnchor='middle'
                  fill={palette.caution}
                >
                  {t('stationVolumes.chart.equationTag', { from: staOnly(e.from, 0), to: staOnly(e.to, 0) })}
                </text>
              )}
            </g>
          ))}

          {/* The offset axis. Left of the alignment is the TOP. */}
          <text x={plot.left - 6} y={plot.top + 9} fontSize={9} textAnchor='end' fill={palette.haul}>
            {`${t('stationVolumes.strip.axisLeft')} ${formatFixed(swathLeftM, 1)}`}
          </text>
          {/* CL sits where the offset axis CROSSES ZERO, not at the pane's
              geometric middle: an asymmetric swath (left 3 m, right 8 m) has
              its centreline off centre, and a middle-anchored "CL" would label
              offset +2.5 m as the alignment while the tooltip below it read
              the truth. Clamped into the box like the Python twin
              (`station_strip.py`), for a swath that does not contain zero. */}
          <text x={plot.left - 6} y={centreY + 3} fontSize={9} textAnchor='end' fill={palette.haul}>
            {t('stationVolumes.strip.axisCentre')}
          </text>
          <text x={plot.left - 6} y={plot.top + plot.height - 2} fontSize={9} textAnchor='end' fill={palette.haul}>
            {`${t('stationVolumes.strip.axisRight')} ${formatFixed(swathRightM, 1)}`}
          </text>
        </svg>

        {/* The per-bin verdict row (spec §3.4). Only drawn when the run
            carries verdicts at all — an empty grey rule under the strip would
            read as a result. */}
        {anyVerdict && (
          <div
            className='absolute'
            data-testid='sv-strip-verdicts'
            style={{ left: plot.left, top: plot.top + plot.height, width: plot.width, height: VERDICT_ROW_PX }}
          >
            {verdictRuns.map((r) => {
              // One cell per RUN, positioned rather than flexed, and a cell
              // that CARRIES a verdict floored at `MIN_MARK_PX` — `flex: 1 1 0`
              // per bin gave a 20 000-bin run 20 000 children 0.055 px wide,
              // i.e. a verdict row with nothing in it. A run with NO verdict
              // paints nothing, so it keeps its natural geometry: widening it
              // would only let it overlap a floored neighbour.
              const span = markSpanPx(r.start, r.end, nBins, 0, plot.width, r.value ? MIN_MARK_PX : 0);
              return (
                <div
                  key={r.start}
                  data-testid='sv-strip-verdict-cell'
                  data-bin={r.start}
                  data-bins={r.end - r.start}
                  data-verdict={r.value ?? undefined}
                  title={r.value ? `${SEVERITY[r.value].glyph} ${r.value}` : undefined}
                  className={`absolute inset-y-0 ${r.value ? SEVERITY[r.value].cls : ''}`}
                  style={{
                    left: span.x,
                    width: span.width,
                    // INCONCLUSIVE is hatched as well as coloured: "we could not
                    // tell" must not read as a pale PASS on a greyscale print.
                    backgroundImage:
                      r.value === 'INCONCLUSIVE'
                        ? 'repeating-linear-gradient(45deg, currentColor 0 1px, transparent 1px 4px)'
                        : undefined,
                  }}
                />
              );
            })}
          </div>
        )}

        {/* A plain positioned div, not a portal: the tip belongs to the pane
            it describes, and a portal would put it outside anything that
            measures or tests this component. */}
        {tipBin && hoverOffset != null && hoverBin != null && (
          <div
            className='pointer-events-none absolute z-10 rounded border border-border bg-popover px-2 py-1 text-[10px] leading-tight text-popover-foreground shadow-sm'
            data-testid='sv-strip-tooltip'
            style={{
              left: plot.left + (hoverBin + 0.5) * colW,
              top: plot.top + imageRowForOffsetIndex(hoverOffset, nOffsets) * rowH,
              transform: 'translate(-50%, -110%)',
              whiteSpace: 'nowrap',
            }}
          >
            <div className='font-medium'>{staOnly(tipBin.staStartDisplayM)}</div>
            <div>
              {t('stationVolumes.strip.tipOffset', {
                offset: `${formatFixed(Math.abs(tipOffsetM), 2)} ${t(
                  tipOffsetM < 0 ? 'stationVolumes.strip.axisLeft' : 'stationVolumes.strip.axisRight',
                )}`,
              })}
            </div>
            <div>
              {Number.isFinite(tipValue)
                ? t('stationVolumes.strip.tipDz', {
                    mm: formatFixed(tipValue * 1000, 1),
                    word: t(signIsCut(kind, tipValue) ? CUT_LEGEND_SHORT_KEY : FILL_LEGEND_SHORT_KEY),
                  })
                : t('stationVolumes.strip.tipNoData')}
            </div>
            {tipZTop != null && (
              <div data-testid='sv-strip-tip-ztop'>
                {t('stationVolumes.strip.tipZTop', { m: metres(tipZTop) })}
              </div>
            )}
            {tipZBottom != null && (
              <div data-testid='sv-strip-tip-zbottom'>
                {t('stationVolumes.strip.tipZBottom', { m: metres(tipZBottom) })}
              </div>
            )}
            <div>{t('stationVolumes.strip.tipCoverage', { pct: formatFixed(tipBin.coveragePct, 0) })}</div>
            {tipBin.verdict && (
              <div>{`${SEVERITY[tipBin.verdict].glyph} ${tipBin.verdict}`}</div>
            )}
          </div>
        )}
      </div>

      <div className='mt-1 flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 text-[10px] leading-tight text-muted-foreground'>
        <span className='flex flex-col gap-0.5' data-testid='sv-strip-ramp'>
          <canvas
            ref={rampRef}
            aria-hidden
            className='rounded-[2px]'
            style={{ width: RAMP_W_PX, height: RAMP_H_PX }}
          />
          <span className='sv-num flex justify-between' style={{ width: RAMP_W_PX }}>
            <span>{`\u2212${formatFixed(spanM * 1000, 0)}`}</span>
            <span>0</span>
            <span>{`+${formatFixed(spanM * 1000, 0)} mm`}</span>
          </span>
        </span>
        <span data-testid='sv-strip-legend'>
          {legend} {t('stationVolumes.strip.spanLabel', { span: formatFixed(spanM, 3) })}
        </span>
        <span data-testid='sv-strip-orientation'>{t('stationVolumes.strip.orientation')}</span>
      </div>
    </div>
  );
};
